import type { UseFormReturn } from "@gigpro/ui/Form";
import { type SkillListItemReadonly, type SkillUniformOptions, useSkillsUniformRetrieve } from "@sidegigllc/api/client";
import { keepPreviousData } from "@tanstack/react-query";
import type { UniformSelection } from "api/resources/uniforms";
import { CustomUniformBuilder, type CustomUniformValue } from "components/CustomUniformBuilder";
import { useCustomUniformCatalog } from "components/CustomUniformBuilder/useCustomUniformCatalog";
import { EmojiLoader } from "components/Loader/emoji";
import { UniformSection, type UniformSectionValue } from "components/UniformSection";
import type { Pro } from "domains/postGig/providers/PostGigData/types";
import React from "react";
import { useWatch } from "react-hook-form";

export interface UniformFieldsProps {
  form: UseFormReturn<Pro, Array<SkillListItemReadonly>, Pro>;
}

type CustomItems = NonNullable<Extract<UniformSelection, { selection_type: "CUSTOM" }>["custom_items"]>;

const emptyCustom: CustomUniformValue = { colorScheme: "BLACK_ON_BLACK", itemIds: [] };

type StoredUniform = Pro["uniform"];

interface PrefilledUniform {
  selection: UniformSectionValue;
  copiedItems?: CustomItems;
  colorScheme?: CustomUniformValue["colorScheme"];
  itemIds?: Array<number>;
}

const prefillUniform = (
  stored: StoredUniform,
  fallback: { selection_type: string; color_scheme?: CustomUniformValue["colorScheme"] | null },
): PrefilledUniform => {
  if (stored?.selection_type === "NOT_REQUIRED") return { selection: "NOT_REQUIRED" };
  if (stored) {
    const isCustomStored = stored.selection_type === "CUSTOM";
    return {
      selection: stored.selection_type === "PRESET" ? stored.color_scheme : "CUSTOM",
      copiedItems: isCustomStored ? (stored.custom_items ?? []) : [],
      colorScheme: stored.color_scheme,
      itemIds: isCustomStored ? stored.item_ids : undefined,
    };
  }
  const { selection_type, color_scheme } = fallback;
  return {
    selection: selection_type === "PRESET" && color_scheme ? color_scheme : "CUSTOM",
    copiedItems: [],
    colorScheme: color_scheme ?? emptyCustom.colorScheme,
  };
};

interface UniformMirrorInput {
  selection: UniformSectionValue;
  custom: CustomUniformValue;
  copiedItems: CustomItems;
  customCatalogReady: boolean;
  availableItemIds: ReadonlySet<number>;
  preset: { id: number } | undefined;
}

const uniformMirror = ({
  selection,
  custom,
  copiedItems,
  customCatalogReady,
  availableItemIds,
  preset,
}: UniformMirrorInput): UniformSelection | undefined => {
  if (selection === "CUSTOM") {
    // Wait for the catalog so the filter below sees the real available ids; writing now would clear a
    // saved template's stored item_ids to [] until the catalog resolves.
    if (!customCatalogReady) return undefined;
    return {
      selection_type: "CUSTOM",
      color_scheme: custom.colorScheme,
      item_ids: custom.itemIds.filter((id) => availableItemIds.has(id)),
      custom_items: copiedItems,
    };
  }
  if (selection === "NOT_REQUIRED") {
    // Explicit "no uniform required": a bare block, no color_scheme/item_ids/preset_id (CORE-6537).
    return { selection_type: "NOT_REQUIRED" };
  }
  return preset ? { selection_type: "PRESET", color_scheme: selection, preset_id: preset.id } : undefined;
};

type UniformData = SkillUniformOptions | undefined;

interface PrefilledUniformInput {
  data: UniformData;
  skillId: number | null | undefined;
  mirroredUniform: StoredUniform;
}

const usePrefilledUniform = ({ data, skillId, mirroredUniform }: PrefilledUniformInput) => {
  const [selection, setSelection] = React.useState<UniformSectionValue>();
  const [custom, setCustom] = React.useState<CustomUniformValue>(emptyCustom);
  // Garments a uniform copied from a past post names by label: the builder has no ids for them, so they ride along unchanged.
  const [copiedItems, setCopiedItems] = React.useState<CustomItems>([]);

  // Pre-fill once per position (keyed on skillId, not data): stored uniform on first load, else the
  // position default. keepPreviousData can return the previous position's payload, hence the id match.
  const [prefilledForSkill, setPrefilledForSkill] = React.useState<number>();
  if (data && skillId != null && data.position.id === skillId && prefilledForSkill !== skillId) {
    setPrefilledForSkill(skillId);
    // The mirror below doesn't write until `selection` is set, so `mirroredUniform` is still the stored value.
    const seed = prefillUniform(prefilledForSkill === undefined ? mirroredUniform : undefined, data.default);
    const { colorScheme, itemIds } = seed;
    setSelection(seed.selection);
    if (seed.copiedItems) setCopiedItems(seed.copiedItems);
    if (colorScheme) setCustom((prev) => ({ ...prev, colorScheme, itemIds: itemIds ?? prev.itemIds }));
  }
  return { selection, setSelection, custom, setCustom, copiedItems, setCopiedItems };
};

interface UniformMirrorEffectInput {
  form: UniformFieldsProps["form"];
  data: UniformData;
  skillId: number | null | undefined;
  mirroredUniform: StoredUniform;
  selection: UniformSectionValue | undefined;
  custom: CustomUniformValue;
  copiedItems: CustomItems;
  customCatalogReady: boolean;
  availableItemIds: ReadonlySet<number>;
}

const useUniformMirror = ({
  form,
  data,
  skillId,
  mirroredUniform,
  selection,
  custom,
  copiedItems,
  customCatalogReady,
  availableItemIds,
}: UniformMirrorEffectInput) => {
  // Gate for the mirror below (PR #4729 review). `data` can be a stale keepPreviousData response for
  // the *previous* position mid skill-change — mirroring then would send an old preset/item_ids with
  // the new skill_id, so require it to describe the CURRENT skill. (`customCatalogReady` above guards the
  // CUSTOM branch so the filter never empties stored ids while the catalog is still loading.)
  const dataMatchesSkill = data?.position.id === skillId;

  // Mirror the UI selection into the form's write-only `uniform` block (consumed at submit by
  // getGigsForShift). preset_id is resolved here from the fetched presets — the submit layer has no
  // access to the catalog. Custom "Add your own" garments are already persisted as catalog items
  // (CORE-6244), so they ride in `item_ids`; the UI produces no free-text custom_items, and only
  // those a copied past post brought in are kept (CORE-9096).
  // The catalog is scheme-scoped, so filter out any ids left selected from the other colour scheme
  // (toggling colour preserves the selection) before they reach the payload.
  //
  // We read the mirrored value back and re-assert whenever it drifts from the current selection. This
  // covers the initial default (no user click), colour/item edits, AND an external `form.reset`: the
  // parent FormDialog resets to the position defaults on skill change (and can re-fire on re-render),
  // dropping `uniform` because getDefaults omits it. Without re-asserting, keeping the pre-selected
  // default would persist nothing until the user manually re-picked it (CORE-6358).
  React.useEffect(() => {
    // Skip while `data` is stale for the current skill (mid position-change) — leaves the form's reset
    // `uniform` cleared until the new position's default is ready, rather than re-asserting the old one.
    if (selection == null || !dataMatchesSkill) return;
    const next = uniformMirror({
      selection,
      custom,
      copiedItems,
      customCatalogReady,
      availableItemIds,
      preset: data?.uniform?.presets.find((p) => p.color_scheme === selection),
    });
    // Key order is deterministic (built here, read back from the same writes), so a stringify compare
    // is a safe, dependency-free deep equal and guards against a setValue → watch → effect loop.
    if (next && JSON.stringify(mirroredUniform) !== JSON.stringify(next)) {
      form.setValue("uniform", next);
    }
  }, [
    form,
    selection,
    custom,
    copiedItems,
    data,
    availableItemIds,
    mirroredUniform,
    dataMatchesSkill,
    customCatalogReady,
  ]);
};

/**
 * Uniform section (W1/W3) shown in Request-a-Pro in place of the Requirements-for-Work section.
 * Fetches the position's presets from
 * `GET /skills/{id}/uniform/` and, when Custom is chosen, the garment catalog from
 * `GET /uniforms/catalog/?color_scheme=` — the backend scopes the catalog to the scheme (CORE-6275),
 * so flipping the colour toggle refetches the matching garments.
 *
 * "Add your own" garments persist per-location via `GET/POST/DELETE /locations/{id}/uniform-items/`
 * (CORE-6244): the builder renders the union of the global catalog and this location's custom
 * garments, and add/remove hit the location endpoint.
 *
 * The UI selection is mirrored into the `uniform` form field (CORE-6325) as the write-only block the
 * backend snapshots on gig create (CORE-6240): a preset card resolves to `{ PRESET, preset_id }`, the
 * custom builder to `{ CUSTOM, item_ids }`. When editing a saved template that persisted a uniform
 * (CORE-6358), the section pre-fills from that stored selection instead of the position default.
 */
export function UniformFields({ form }: Readonly<UniformFieldsProps>) {
  const [skillId, locationId, mirroredUniform] = useWatch({
    control: form.control,
    name: ["skill_id", "location_id", "uniform"],
  });
  const { data, isLoading } = useSkillsUniformRetrieve(skillId ?? 0, {
    query: { enabled: skillId != null, staleTime: Number.POSITIVE_INFINITY, placeholderData: keepPreviousData },
  });

  const { selection, setSelection, custom, setCustom, copiedItems, setCopiedItems } = usePrefilledUniform({
    data,
    skillId,
    mirroredUniform,
  });

  const isCustom = selection === "CUSTOM";
  const {
    items,
    availableItemIds,
    deletableItemIds,
    ready: customCatalogReady,
    addCustom,
    removeCustom,
  } = useCustomUniformCatalog({ locationId, enabled: isCustom, value: custom, setValue: setCustom });

  useUniformMirror({
    form,
    data,
    skillId,
    mirroredUniform,
    selection,
    custom,
    copiedItems,
    customCatalogReady,
    availableItemIds,
  });

  if (skillId == null) return null;
  if (isLoading || !data) return <EmojiLoader />;

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-large font-semibold">Uniform</h3>
      <UniformSection
        uniform={data.uniform}
        value={selection}
        onValueChange={(next) => {
          // Leaving Custom drops the copied garments: the manager's way to remove them.
          if (next !== "CUSTOM") setCopiedItems([]);
          setSelection(next);
        }}
      />

      {isCustom && (
        <CustomUniformBuilder
          items={items}
          value={custom}
          onChange={setCustom}
          deletableItemIds={deletableItemIds}
          onAddCustom={addCustom}
          onRemoveCustom={(id) => void removeCustom(id)}
        />
      )}
      {isCustom && copiedItems.length > 0 && (
        <p className="text-small text-gray">From your last post: {copiedItems.map(({ label }) => label).join(", ")}</p>
      )}
    </div>
  );
}
